/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useLazyQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';

import { getLogger } from '@confluence/logger';
import { useTransitionId } from '@confluence/route-manager';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { CollabDraftQuery } from '@confluence/fabric-providers/entry-points/CollabDraftQuery';
import type {
	CollabDraftQueryRes,
	CollabDraftQueryVariables,
} from '@confluence/fabric-providers/entry-points/CollabDraftQuery';
import { expVal } from '@confluence/feature-experiments';

import type {
	useInjectDraftContentgetDraftContentAppearancePropertyQuery as useInjectDraftContentgetDraftContentAppearancePropertyQuery$data,
	useInjectDraftContentgetDraftContentAppearancePropertyQueryVariables as useInjectDraftContentgetDraftContentAppearancePropertyQuery$variables,
} from './__types__/useInjectDraftContentgetDraftContentAppearancePropertyQuery';
import type {
	useInjectDraftContentConfluenceDraftQuery as useInjectDraftContentConfluenceDraftQuery$data,
	useInjectDraftContentConfluenceDraftQueryVariables as useInjectDraftContentConfluenceDraftQuery$variables,
} from './__types__/useInjectDraftContentConfluenceDraftQuery';
import type { ConfluenceDraftQuery as ConfluenceDraftQueryType } from './__types__/ConfluenceDraftQuery';
export type UseInjectDraftContentProps = {
	contentId: string;
	draftShareId?: string;
	collabEditInitialized: boolean;
	editorActions?: EditorActions;
};

export type UseInjectDraftContent = {
	draftTitle: string | null;
	draftBody: string | null; // adf json of body content in string format,
	draftAppearance: string | null;
};

const logger = getLogger('native-collab');

const parseMonolithContent = (data: ConfluenceDraftQueryType, editorActions?: EditorActions) => {
	const content = data?.content?.nodes?.[0];
	const contentBody = content?.body?.atlas_doc_format?.value;
	const contentTitle = content?.title;
	let contentAppearance = null;
	try {
		const appearance = content?.appearanceDraft?.nodes?.[0]?.value;
		if (appearance) {
			contentAppearance = JSON.parse(appearance);
		}
	} catch (e) {
		logger.error`Error parsing content appearance: ${e}`;
	}

	// at this point since we are getting data after the editor has mounted we need to
	// set the content in the editor through editorActions
	if (contentBody && editorActions) {
		editorActions.replaceDocument(contentBody);
	}

	return {
		draftBody: contentBody ?? null,
		draftTitle: contentTitle ?? null,
		draftAppearance: contentAppearance,
	};
};

const parseCollabDraftBody = (
	draftData: CollabDraftQueryRes | undefined,
	appearanceData: useInjectDraftContentgetDraftContentAppearancePropertyQuery$data | undefined,
) => {
	const draft = draftData?.collabDraft;
	const appearanceContent = appearanceData?.content?.nodes?.[0];
	const draftAppearance = appearanceContent?.appearanceDraft?.nodes?.[0]?.value;

	return {
		draftBody: draft?.document ?? null,
		draftTitle: draft?.metadata?.title ?? null,
		draftAppearance: draftAppearance ? JSON.parse(draftAppearance) : null,
	};
};

/**
 * Fetch the draft title & body and insert into editor upon initial load, if collab service is not yet loaded
 * Draft content is synced with Confluence every 5 seconds and on closing the editor, thus should be reliably up-to-date
 * see preloadEditorData, CollabDraftQuery will be preloaded on transition to immediately inject into editor
 */
export const useInjectDraftContent = ({
	contentId,
	draftShareId,
	collabEditInitialized,
	editorActions,
}: UseInjectDraftContentProps): UseInjectDraftContent => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const transitionId = useTransitionId();
	const isInitialLoad = !transitionId;
	const shouldPreloadForSSR = isInitialLoad;
	const isLivePage = useIsCurrentPageLive();
	const bodyPreloadingEnabled =
		fg('confluence_preload_draft_content_in_next_editor') || shouldPreloadForSSR || isLivePage;
	const draftLoadTime = useRef<number | null>(null);
	const getMonolithContentTimeout = useRef<number | null>(null);

	let initialDraft: UseInjectDraftContent = {
		draftBody: null,
		draftTitle: null,
		draftAppearance: null,
	};

	const [
		getMonolithContent,
		{ data: monolithDraftData, loading: monolithContentLoading, error: monolithContentError },
	] = useLazyQuery<
		useInjectDraftContentConfluenceDraftQuery$data,
		useInjectDraftContentConfluenceDraftQuery$variables
	>(
		gql`
			query useInjectDraftContentConfluenceDraftQuery($contentId: ID!, $status: [String]!) @SLA {
				content(id: $contentId, status: $status) {
					nodes {
						appearanceDraft: properties(key: "content-appearance-draft") {
							nodes {
								id
								key
								value
								version {
									number
								}
							}
						}
						id
						type
						status
						title
						body {
							atlas_doc_format {
								value
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				status: ['draft', 'current'],
			},
		},
	);

	const hasConfluenceDraftQueryLoaded =
		!monolithContentLoading && !monolithContentError && !!monolithDraftData;

	const {
		data: collabDraftData,
		loading: collabDraftLoading,
		error: collabDraftError,
	} = useQuery<CollabDraftQueryRes, CollabDraftQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		CollabDraftQuery,
		{
			fetchPolicy: 'cache-first',
			variables: {
				id: contentId,
				...(draftShareId ? { draftShareId } : undefined),
				hydrateAdf: true,
			},
			skip: !bodyPreloadingEnabled,
		},
	);

	const {
		data: appearanceData,
		loading: appearanceLoading,
		error: appearanceError,
	} = useQuery<
		useInjectDraftContentgetDraftContentAppearancePropertyQuery$data,
		useInjectDraftContentgetDraftContentAppearancePropertyQuery$variables
	>(
		gql`
			query useInjectDraftContentgetDraftContentAppearancePropertyQuery(
				$contentId: ID
				$status: [String]
			) {
				content(id: $contentId, status: $status) {
					nodes {
						id
						appearanceDraft: properties(key: "content-appearance-draft") {
							nodes {
								id
								key
								value
								version {
									number
								}
							}
						}
					}
				}
			}
		`,
		{
			fetchPolicy: 'cache-first',
			variables: {
				contentId,
				status: ['current', 'draft'],
			},
			skip: !bodyPreloadingEnabled,
		},
	);

	const hasCollabDraftQueryLoaded =
		!collabDraftLoading && !!collabDraftData?.collabDraft && !!appearanceData && !appearanceLoading;

	initialDraft = useMemo(() => {
		return hasConfluenceDraftQueryLoaded
			? parseMonolithContent(monolithDraftData, editorActions)
			: parseCollabDraftBody(collabDraftData, appearanceData);
	}, [
		collabDraftData,
		appearanceData,
		monolithDraftData,
		hasConfluenceDraftQueryLoaded,
		editorActions,
	]);

	useEffect(() => {
		if (!getMonolithContentTimeout.current && !hasCollabDraftQueryLoaded) {
			getMonolithContentTimeout.current = window.setTimeout(
				() => {
					getMonolithContent();
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'monolithFallback',
							actionSubject: 'nativeEditor',
							source: 'confluenceFrontend',
							objectId: contentId,
							attributes: {
								timeout: expVal('confluence_editor_monolith_fallback_timer', 'value', 10000),
							},
						},
					}).fire();
				},
				expVal('confluence_editor_monolith_fallback_timer', 'value', 10000),
			);
		}
	}, [
		hasCollabDraftQueryLoaded,
		getMonolithContentTimeout,
		getMonolithContent,
		createAnalyticsEvent,
		contentId,
	]);

	useEffect(() => {
		if (getMonolithContentTimeout.current && (hasCollabDraftQueryLoaded || collabEditInitialized)) {
			clearTimeout(getMonolithContentTimeout.current);
			getMonolithContentTimeout.current = null;
		}
	}, [getMonolithContentTimeout, hasCollabDraftQueryLoaded, collabEditInitialized]);

	useEffect(() => {
		return () => {
			if (getMonolithContentTimeout.current) {
				clearTimeout(getMonolithContentTimeout.current);
			}
		};
	}, [getMonolithContentTimeout]);

	useEffect(() => {
		if (!bodyPreloadingEnabled) return;

		if (!collabDraftError && hasCollabDraftQueryLoaded && !appearanceError) {
			draftLoadTime.current = performance.now();
			logger.debug`Draft content retrieved from CollabDraftQuery at ${performance.now() / 1000}`;
		}
	}, [hasCollabDraftQueryLoaded, collabDraftError, appearanceError, bodyPreloadingEnabled]);

	useEffect(() => {
		if (collabEditInitialized && !!draftLoadTime.current) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'editorContentVisibleDisabledTime',
					actionSubject: 'nativeEditor',
					source: 'confluenceFrontend',
					objectId: contentId,
					attributes: {
						timeDiff: performance.now() - draftLoadTime.current,
					},
				},
			}).fire();
		}
		// don't depend on createAnalyticsEvent to avoid unnecessary re-triggers of this effect
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collabEditInitialized, contentId]);

	return initialDraft;
};
